'use client';

export type GptSize = [number, number] | 'fluid';

declare global {
  interface Window {
    googletag?: GoogleTag;
  }
}

// Before gpt.js loads, `window.googletag` is just a command queue; the script fills in the rest.
type GoogleTag = { cmd: Array<() => void> } & Partial<GoogleTagApi>;

type GoogleTagApi = {
  defineSlot: (adUnitPath: string, sizes: ReadonlyArray<GptSize>, divId: string) => GoogleTagSlot | null;
  pubads: () => GoogleTagPubAds;
  setConfig: (config: PageSettingsConfig) => void;
  enableServices: () => void;
  display: (divId: string) => void;
  destroySlots: (slots?: Array<GoogleTagSlot>) => boolean;
};

/**
 * Page-level config, the replacement for the deprecated `pubads()` setters.
 * `collapseDiv: 'ON_NO_FILL'` is the argument-less `collapseEmptyDivs()` GPT
 * documents (collapse only once a slot comes back unfilled), not `BEFORE_FETCH`.
 */
export type PageSettingsConfig = {
  singleRequest?: boolean;
  collapseDiv?: 'BEFORE_FETCH' | 'ON_NO_FILL' | 'DISABLED';
};

/**
 * Slot-level config, the replacement for `setTargeting`/`clearTargeting`.
 *
 * `targeting` MERGES into whatever the slot already carries — it does not
 * replace it. A key only goes away when it is explicitly set to null, which is
 * what `buildTargetingUpdate` in `useSrpGoogleAds` exists to work out.
 */
export type SlotSettingsConfig = {
  targeting?: Record<string, string | Array<string> | null>;
};

export type GoogleTagSlot = {
  addService: (service: GoogleTagPubAds) => void;
  getSlotElementId: () => string;
  setConfig: (config: SlotSettingsConfig) => void;
};

type SlotRenderEndedEvent = {
  slot: GoogleTagSlot;
  isEmpty: boolean;
};

export type GoogleTagPubAds = {
  enableAsyncRendering: () => void;
  /** Re-requests the given slots in place, keeping their containers mounted. */
  refresh: (slots?: Array<GoogleTagSlot>) => void;
  set: (key: string, val: string) => void;
  setRequestNonPersonalizedAds: (value: 0 | 1) => void;
  setPrivacySettings: (settings: { restrictDataProcessing?: boolean }) => void;
  addEventListener: (event: 'slotRenderEnded', listener: (event: SlotRenderEndedEvent) => void) => void;
  removeEventListener: (event: 'slotRenderEnded', listener: (event: SlotRenderEndedEvent) => void) => void;
};

/**
 * Queues work against GPT, seeding the command queue if gpt.js hasn't loaded yet.
 * The callback runs once the script is ready, so the full API is available to it.
 */
export function queueGptCommand(command: (gpt: GoogleTagApi) => void): void {
  const googletag = (window.googletag ??= { cmd: [] });
  googletag.cmd.push(() => command(googletag as GoogleTagApi));
}

/**
 * Reports whether each ad request for `containerId` came back unfilled. Fires on
 * every request, not just the first, so a slot that was empty once can report
 * itself filled on a later refresh. Returns an unsubscribe.
 */
export function onSlotRenderEnded(containerId: string, onRenderEnded: (isEmpty: boolean) => void): () => void {
  let cancelled = false;
  const listener = (event: SlotRenderEndedEvent) => {
    if (event.slot.getSlotElementId() === containerId) onRenderEnded(event.isEmpty);
  };

  queueGptCommand((gpt) => {
    if (cancelled) return;
    gpt.pubads().addEventListener('slotRenderEnded', listener);
  });

  return () => {
    cancelled = true;
    queueGptCommand((gpt) => gpt.pubads().removeEventListener('slotRenderEnded', listener));
  };
}
